import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Megaphone, X, Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DJShoutout({ wsRef }) {
  const { user } = useAuth();
  const [shoutout, setShoutout] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(false);

  const isDJ = user && ['dj', 'admin', 'mod'].includes(user.role);

  const fetchShoutout = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/shoutout`);
      if (response.ok) {
        const data = await response.json();
        setShoutout(data);
      }
    } catch (err) {
      console.log('Failed to fetch shoutout');
    }
  }, []);

  useEffect(() => {
    fetchShoutout();
  }, [fetchShoutout]);

  // Listen for WebSocket shoutout updates
  useEffect(() => {
    if (!wsRef?.current) return;

    const originalOnMessage = wsRef.current.onmessage;
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'shoutout') {
        setShoutout(data.data);
      }
      // Call original handler
      if (originalOnMessage) {
        originalOnMessage(event);
      }
    };
  }, [wsRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/shoutout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message.trim(),
          duration_minutes: duration
        })
      });

      if (response.ok) {
        setMessage('');
        setShowForm(false);
        fetchShoutout();
      }
    } catch (err) {
      console.log('Failed to create shoutout');
    }
    setLoading(false);
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/shoutout`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setShoutout(null);
      }
    } catch (err) {
      console.log('Failed to clear shoutout');
    }
    setLoading(false);
  };

  return (
    <div data-testid="dj-shoutout-container">
      {/* Active Shoutout Display */}
      {shoutout && (
        <div 
          className="flex items-start gap-3 p-3 rounded-xl mb-3 animate-pulse"
          style={{ 
            background: 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.2), rgba(var(--secondary-rgb), 0.2))',
            border: '1px solid var(--primary)'
          }}
          data-testid="active-shoutout"
        >
          <Megaphone className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: 'var(--text-main)' }}>
              {shoutout.message}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              — {shoutout.created_by_name}
            </p>
          </div>
          {isDJ && (
            <button
              onClick={handleClear}
              disabled={loading}
              className="p-1 rounded hover:bg-white/10"
              style={{ color: 'var(--text-muted)' }}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* DJ Shoutout Form */}
      {isDJ && (
        <>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 text-xs transition-colors hover:opacity-80"
              style={{ color: 'var(--primary)' }}
              data-testid="create-shoutout-btn"
            >
              <Megaphone className="w-4 h-4" />
              <span>{shoutout ? 'Update Shoutout' : 'Create Shoutout'}</span>
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Shoutout message (e.g., 'This hour brought to you by...')"
                rows={2}
                className="w-full p-2 rounded-lg text-sm resize-none outline-none"
                style={{ 
                  backgroundColor: 'var(--surface-highlight)',
                  color: 'var(--text-main)'
                }}
                data-testid="shoutout-input"
              />
              <div className="flex items-center gap-2">
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="px-2 py-1 rounded text-xs"
                  style={{ 
                    backgroundColor: 'var(--surface-highlight)',
                    color: 'var(--text-main)'
                  }}
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                </select>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded text-xs"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="flex items-center gap-1 px-3 py-1 rounded text-xs disabled:opacity-50"
                  style={{ 
                    backgroundColor: 'var(--primary)',
                    color: 'white'
                  }}
                  data-testid="submit-shoutout-btn"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  <span>Post</span>
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
